/**
 * Closeout "Hand to…" (threads/hand-it-off; mockup
 * https://claude.ai/artifact/Mu9v1tYqoejKiYU9qyzeB9).
 *
 * When the office can't move a job on its own ("Customer wants Net 30, OK?")
 * it hands the job to a person with one line. The job goes to the top of that
 * person's Yours pile. They answer Done or Hand back, and the job comes back
 * to the sender with the answer on the card until the sender opens it.
 * No task, no bell.
 *
 *   HandToButton  — "Hand to ▾" (Closeout header, job page).
 *   HandToDialog  — pick a person, one required line.
 *   HandoffBanner — the big card: the ask / the answer, with Done · Hand back ·
 *                   Take it back (11/4: one big card, the other strips point to it).
 *   JobHandoffStrip — the job page's version (only for jobs in Closeout).
 *
 * Visual first: pink = with someone, green = an answer came back.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeftToLine, ArrowRightToLine, Check, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    handoffReplyLine,
    handoffReturnLine,
    handoffWaitLabel,
    useWhoseTurn,
    type AskOption,
    type Ball,
    type JobNote,
    type BallHandoff,
    type HandoffPerson,
} from "@/lib/closeout-ball";
import { refreshCloseOutQueue } from "@/pages/close-out/queueRefresh";
import { JobNotesDialog, WhyStrip } from "@/pages/close-out/ball";

export const HANDOFF_REASON_MAX = 300;

interface JobHandoffResponse {
    open: BallHandoff | null;
    reply: BallHandoff | null;
    people: HandoffPerson[];
}

export function useJobHandoff(jobId: string, enabled = true) {
    return useQuery({
        queryKey: ["close-out-handoff", jobId],
        queryFn: () => api.get<JobHandoffResponse>(`/api/close-out/jobs/${jobId}/handoff`),
        enabled: enabled && !!jobId,
        staleTime: 30_000,
        retry: false,
    });
}

function useRefresh(jobId: string) {
    const qc = useQueryClient();
    return () => {
        refreshCloseOutQueue(qc, { jobId });
        qc.invalidateQueries({ queryKey: ["close-out-handoff", jobId] });
        qc.invalidateQueries({ queryKey: ["close-out-ball-for-task", jobId] });
        qc.invalidateQueries({ queryKey: ["job-handoff-queue", jobId] });
    };
}

function errMsg(e: any, fallback: string): string {
    return e?.body?.message ?? fallback;
}

// ─── Button + dialog ────────────────────────────────────────────────────
export function HandToButton({
    jobId,
    jobName,
    disabled,
    className,
    asks,
}: {
    jobId: string;
    jobName: string;
    /** An open hand-off already sits on the job. */
    disabled?: boolean;
    className?: string;
    /** Whose turn: the job's open steps to pick from (else one typed line). */
    asks?: AskOption[];
}) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <Button
                size="sm"
                variant="outline"
                disabled={disabled}
                onClick={() => setOpen(true)}
                className={cn(
                    "border-pink-300 bg-pink-50 text-pink-800 hover:bg-pink-100 dark:border-pink-800 dark:bg-pink-950/40 dark:text-pink-200",
                    className,
                )}
                title={disabled ? "This job is already with someone." : "Hand this job to a person"}
                data-testid="hand-to-button"
            >
                <ArrowRightToLine className="mr-1 h-3.5 w-3.5" />
                Hand to ▾
            </Button>
            {open && <HandToDialog jobId={jobId} jobName={jobName} asks={asks} onClose={() => setOpen(false)} />}
        </>
    );
}

export function HandToDialog({
    jobId,
    jobName,
    defaultToUserId,
    onClose,
    onDone,
    asks,
}: {
    jobId: string;
    jobName: string;
    defaultToUserId?: string | null;
    /** Whose turn (10/28): pick what you need from the job's open steps.
     *  GWC watches that step and sends the job back when it clears. */
    asks?: AskOption[];
    onClose: () => void;
    /** Called after it is handed off (the Ask someone dialog closes too). */
    onDone?: () => void;
}) {
    const q = useJobHandoff(jobId);
    const refresh = useRefresh(jobId);
    const people = q.data?.people ?? [];
    const [toId, setToId] = useState<string | null>(defaultToUserId ?? null);
    const [reason, setReason] = useState("");
    const steps = asks && asks.length > 0 ? asks : null;
    const [askKind, setAskKind] = useState<string | null>(null);
    useEffect(() => {
        if (defaultToUserId) setToId(defaultToUserId);
    }, [defaultToUserId]);
    const to = people.find((p) => p.id === toId) ?? null;
    // A picked step needs no words; "Something else" (or the old dialog) does.
    const watched = !!steps && !!askKind && askKind !== "other";
    const canSend = !!toId && (steps ? !!askKind && (watched || !!reason.trim()) : !!reason.trim());
    const m = useMutation({
        mutationFn: () =>
            api.post(`/api/close-out/jobs/${jobId}/handoff`, {
                toUserId: toId,
                reason: reason.trim(),
                ...(steps && askKind ? { askKind } : {}),
            }),
        onSuccess: () => {
            toast.success(
                watched
                    ? `Handed to ${to?.first_name ?? "them"}. When it's done, it comes back to you by itself.`
                    : `Handed to ${to?.first_name ?? "them"}. It's at the top of their pile.`,
            );
            refresh();
            onDone?.();
            onClose();
        },
        onError: (e: any) => toast.error(errMsg(e, "Couldn't hand it off. Try again.")),
    });
    const already = q.data?.open ?? null;
    return (
        <Dialog open onClose={onClose} title={`Hand this job to… · ${jobName}`}>
            <div className="space-y-3" data-testid="hand-to-dialog">
                {already ? (
                    <p className="text-sm">
                        This job is already with <b>{already.toName}</b>: “{already.reason}”
                    </p>
                ) : (
                    <>
                        <div className="flex flex-wrap gap-1.5">
                            {people.map((p) => (
                                <button
                                    key={p.id}
                                    type="button"
                                    onClick={() => setToId(p.id)}
                                    aria-pressed={toId === p.id}
                                    className={cn(
                                        "inline-flex items-center gap-2 rounded-full border py-1 pl-1 pr-3 text-[13px] font-bold",
                                        toId === p.id
                                            ? "border-pink-600 bg-pink-50 text-pink-800 dark:bg-pink-950/50 dark:text-pink-200"
                                            : "border-border hover:bg-muted",
                                    )}
                                >
                                    <span
                                        className={cn(
                                            "flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-extrabold",
                                            toId === p.id ? "bg-pink-700 text-white" : "bg-muted text-foreground/60",
                                        )}
                                    >
                                        {(p.first_name ?? "?").charAt(0)}
                                    </span>
                                    {p.first_name}
                                </button>
                            ))}
                            {q.isLoading && <span className="text-sm text-muted-foreground">Loading people…</span>}
                        </div>
                        {steps && (
                            <div data-testid="hand-to-asks">
                                <div className="mb-1.5 text-[12.5px] font-bold">
                                    What do you need{to ? ` from ${to.first_name}` : ""}?
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {steps.map((a) => (
                                        <button
                                            key={a.kind}
                                            type="button"
                                            onClick={() => setAskKind(a.kind)}
                                            aria-pressed={askKind === a.kind}
                                            data-testid={`hand-to-ask-${a.kind}`}
                                            className={cn(
                                                "rounded-full border px-3 py-1 text-[12.5px] font-bold",
                                                askKind === a.kind
                                                    ? "border-pink-600 bg-pink-50 text-pink-800 dark:bg-pink-950/50 dark:text-pink-200"
                                                    : a.kind === "other"
                                                      ? "border-dashed border-border text-foreground/75 hover:bg-muted"
                                                      : "border-border hover:bg-muted",
                                            )}
                                        >
                                            {a.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {(!steps || askKind) && (
                            <Input
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                maxLength={HANDOFF_REASON_MAX}
                                placeholder={
                                    watched
                                        ? "Anything to add? (not needed)"
                                        : "One line. e.g. Is the $85 trip fee right?"
                                }
                                aria-label="What you need from them"
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && canSend && !m.isPending) m.mutate();
                                }}
                            />
                        )}
                        <p className="text-[12px] text-muted-foreground">
                            {steps
                                ? watched
                                    ? "GWC watches for it. When it's done, the job comes back to you by itself."
                                    : askKind === "other"
                                      ? `${to?.first_name ?? "They"} taps Done when it's handled, and it comes back to you.`
                                      : "Pick one. Nobody gets a bell or a task."
                                : `Say what you need${to ? ` from ${to.first_name}` : ""}. It goes to the top of their pile. Nobody gets a bell or a task.`}
                        </p>
                    </>
                )}
                <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={onClose}>
                        Cancel
                    </Button>
                    {!already && (
                        <Button
                            size="sm"
                            disabled={!canSend || m.isPending}
                            onClick={() => m.mutate()}
                            className="bg-pink-700 text-white hover:bg-pink-800"
                        >
                            <ArrowRightToLine className="mr-1 h-3.5 w-3.5" />
                            {to ? `Hand to ${to.first_name}` : "Hand it off"}
                        </Button>
                    )}
                </div>
            </div>
        </Dialog>
    );
}

// ─── The ask / the answer on the job ────────────────────────────────────
// 11/4 (mockup handoff-visible-mockup.png): the hand-off is ONE big card at
// the top of the job — the sender's initial, the ask in big type, how long
// it has waited, and big buttons. Pink while it is with someone, green when
// the answer came back. The other strips only point up at it.
function timeLabel(iso: string | null): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    return d.toDateString() === new Date().toDateString()
        ? `today at ${time}`
        : `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })} at ${time}`;
}

function initial(name: string | null | undefined): string {
    return (name ?? "?").trim().charAt(0).toUpperCase() || "?";
}

type Tone = "pink" | "green";

function CardShell({
    tone,
    bar,
    barIcon,
    face,
    who,
    big,
    pills,
    children,
    testId,
}: {
    tone: Tone;
    bar: string;
    barIcon: React.ReactNode;
    face: string;
    who: React.ReactNode;
    big: React.ReactNode;
    pills: React.ReactNode;
    children?: React.ReactNode;
    testId: string;
}) {
    const pink = tone === "pink";
    return (
        <div className="border-b border-border p-3" data-testid={testId}>
            <div
                className={cn(
                    "overflow-hidden rounded-2xl border-2",
                    pink
                        ? "border-pink-700 bg-pink-50 dark:bg-pink-950/40"
                        : "border-emerald-700 bg-emerald-50 dark:bg-emerald-950/40",
                )}
            >
                <div
                    className={cn(
                        "flex items-center gap-2 px-4 py-1.5 text-[14px] font-extrabold text-white",
                        pink ? "bg-pink-700" : "bg-emerald-700",
                    )}
                    data-testid="handoff-card-bar"
                >
                    {barIcon}
                    {bar}
                </div>
                <div className="flex gap-3 px-4 py-4 sm:gap-4 sm:px-5">
                    <span
                        aria-hidden
                        className={cn(
                            "flex h-12 w-12 flex-none items-center justify-center rounded-full text-[22px] font-black text-white ring-4 ring-white sm:h-16 sm:w-16 sm:text-[30px] dark:ring-background",
                            pink ? "bg-pink-700" : "bg-emerald-700",
                        )}
                        data-testid="handoff-face"
                    >
                        {face}
                    </span>
                    <div className="min-w-0 flex-1">
                        <div
                            className={cn(
                                "text-[15px] font-bold",
                                pink ? "text-pink-800 dark:text-pink-200" : "text-emerald-800 dark:text-emerald-200",
                            )}
                        >
                            {who}
                        </div>
                        <div
                            className="mt-1 break-words text-[22px] font-black leading-tight text-foreground sm:text-[30px]"
                            data-testid="handoff-card-ask"
                        >
                            {big}
                        </div>
                        <div className="mt-2.5 flex flex-wrap items-center gap-2">{pills}</div>
                    </div>
                </div>
                {children}
            </div>
        </div>
    );
}

function Pill({ children, tone, testId }: { children: React.ReactNode; tone: "amber" | "plain"; testId?: string }) {
    return (
        <span
            data-testid={testId}
            className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[13.5px]",
                tone === "amber"
                    ? "bg-amber-100 font-extrabold text-amber-800 dark:bg-amber-900/50 dark:text-amber-200"
                    : "border border-border bg-card font-semibold text-muted-foreground",
            )}
        >
            {children}
        </span>
    );
}

/** The row of big buttons, lined up under the words (not under the face). */
function BigRow({ children, testId }: { children: React.ReactNode; testId?: string }) {
    return (
        <div
            className="flex flex-col gap-2.5 px-4 pb-4 sm:flex-row sm:flex-wrap sm:items-center sm:pl-[100px] sm:pr-5"
            data-testid={testId}
        >
            {children}
        </div>
    );
}

function BigButton({
    kind,
    icon,
    label,
    sub,
    on,
    disabled,
    onClick,
    testId,
}: {
    kind: "done" | "pink" | "plain";
    icon?: React.ReactNode;
    label: string;
    sub?: string;
    on?: boolean;
    disabled?: boolean;
    onClick: () => void;
    testId?: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-pressed={on}
            data-testid={testId}
            className={cn(
                "inline-flex min-h-14 flex-wrap items-center justify-center gap-x-2.5 rounded-xl border-2 px-5 py-1.5 text-[17px] font-extrabold disabled:opacity-60",
                kind === "done" && "border-emerald-700 bg-emerald-700 text-white hover:bg-emerald-800",
                kind === "pink" &&
                    "border-pink-700 bg-card text-pink-800 hover:bg-pink-50 dark:text-pink-200 dark:hover:bg-pink-950/50",
                kind === "plain" && "border-border bg-card text-foreground hover:bg-muted",
                on && "ring-2 ring-offset-2 ring-offset-pink-50 dark:ring-offset-pink-950",
                on && (kind === "done" ? "ring-emerald-700" : "ring-pink-700"),
            )}
        >
            <span className="inline-flex items-center gap-2.5 whitespace-nowrap">
                {icon}
                {label}
            </span>
            {sub && <span className="whitespace-nowrap text-[13px] font-semibold opacity-85">{sub}</span>}
        </button>
    );
}

export function HandoffBanner({
    jobId,
    handoff,
    viewerId,
    onGotIt,
}: {
    jobId: string;
    handoff: BallHandoff;
    viewerId: string | null;
    /** Answered: hides it for good (the job page; Closeout marks it on open). */
    onGotIt?: () => void;
}) {
    const refresh = useRefresh(jobId);
    const turnOn = useWhoseTurn().on;
    const [mode, setMode] = useState<null | "done" | "handed_back">(null);
    const [note, setNote] = useState("");
    const answer = useMutation({
        mutationFn: (outcome: "done" | "handed_back") =>
            api.post(`/api/close-out/handoffs/${handoff.id}/answer`, { outcome, note: note.trim() }),
        onSuccess: (_r, outcome) => {
            toast.success(
                outcome === "done" ? `Done. ${handoff.fromName} will see it.` : `Back with ${handoff.fromName}.`,
            );
            setMode(null);
            setNote("");
            refresh();
        },
        onError: (e: any) => toast.error(errMsg(e, "Couldn't save that. Try again.")),
    });
    // Whose turn on: Done is one tap, no note (same call as DoneSendBack).
    const doneNow = useMutation({
        mutationFn: () => api.post(`/api/close-out/handoffs/${handoff.id}/answer`, { outcome: "done" }),
        onSuccess: () => {
            toast.success(`Sent back to ${handoff.fromName}.`);
            refresh();
        },
        onError: (e: any) => toast.error(errMsg(e, "Couldn't send it back. Try again.")),
    });
    const takeBack = useMutation({
        mutationFn: () => api.post(`/api/close-out/handoffs/${handoff.id}/take-back`, {}),
        onSuccess: () => {
            toast.success("Taken back. It's yours again.");
            refresh();
        },
        onError: (e: any) => toast.error(errMsg(e, "Couldn't take it back. Try again.")),
    });

    const iAmTo = !!viewerId && viewerId === handoff.toUserId;
    const iAmFrom = !!viewerId && viewerId === handoff.fromUserId;

    if (handoff.state === "answered") {
        const replier = handoff.replyByName || handoff.toName;
        const typed = !(handoff.doneLine && !(handoff.reply ?? "").trim());
        return (
            <CardShell
                tone="green"
                testId="handoff-answer"
                bar={iAmFrom ? "Came back to you" : `Came back to ${handoff.fromName}`}
                barIcon={<ArrowLeftToLine className="h-4 w-4" aria-hidden />}
                face={initial(replier)}
                who={
                    typed
                        ? `${replier} ${handoff.outcome === "handed_back" ? "handed it back" : "answered"}:`
                        : `${replier} did it:`
                }
                big={typed ? `“${handoffReplyLine(handoff).replace(/^[^:]*:\s*/, "")}”` : handoffReturnLine(handoff)}
                pills={
                    <>
                        <Pill tone="plain">
                            {typed ? (iAmFrom ? "You asked" : `${handoff.fromName} asked`) : "Asked"}: {handoff.reason}
                        </Pill>
                        {typed && handoff.replyAt && <Pill tone="plain">{timeLabel(handoff.replyAt)}</Pill>}
                    </>
                }
            >
                {onGotIt && (
                    <BigRow>
                        <BigButton
                            kind="plain"
                            icon={<Check className="h-5 w-5" aria-hidden />}
                            label="Got it"
                            onClick={onGotIt}
                            testId="handoff-got-it"
                        />
                    </BigRow>
                )}
            </CardShell>
        );
    }

    const waited = handoffWaitLabel(handoff.at);
    const waitWho = iAmTo ? "you" : handoff.toName;
    const waitPill =
        waited && waited !== "today" ? (
            <Pill tone="amber" testId="handoff-wait">
                <Clock className="h-4 w-4" aria-hidden />
                Waiting on {waitWho} {waited}
            </Pill>
        ) : (
            <Pill tone="plain" testId="handoff-wait">
                <Clock className="h-4 w-4" aria-hidden />
                New today
            </Pill>
        );
    const watched = !!handoff.askKind && handoff.askKind !== "other";

    return (
        <CardShell
            tone="pink"
            testId="handoff-banner"
            bar={iAmTo ? "Handed to you — your move" : `With ${handoff.toName}`}
            barIcon={<ArrowRightToLine className="h-4 w-4" aria-hidden />}
            face={initial(iAmTo || !iAmFrom ? handoff.fromName : handoff.toName)}
            who={
                iAmTo
                    ? `${handoff.fromName} asked you:`
                    : iAmFrom
                      ? `You asked ${handoff.toName}:`
                      : `${handoff.fromName} asked ${handoff.toName}:`
            }
            big={`“${handoff.reason}”`}
            pills={
                <>
                    {waitPill}
                    <Pill tone="plain">
                        {iAmTo
                            ? `Handed ${timeLabel(handoff.at)}`
                            : iAmFrom
                              ? `You handed it ${timeLabel(handoff.at)}`
                              : `${handoff.fromName} handed it ${timeLabel(handoff.at)}`}
                    </Pill>
                </>
            }
        >
            {iAmFrom && !iAmTo && (
                <BigRow>
                    <BigButton
                        kind="plain"
                        icon={<ArrowLeftToLine className="h-5 w-5" aria-hidden />}
                        label="Take it back"
                        disabled={takeBack.isPending}
                        onClick={() => takeBack.mutate()}
                        testId="handoff-take-back"
                    />
                </BigRow>
            )}
            {iAmTo && (
                <BigRow testId={turnOn ? "handoff-turn-actions" : undefined}>
                    {turnOn && watched ? (
                        <span className="text-[14px] font-semibold text-foreground/75">
                            GWC watches for it. When it&apos;s done, it goes back to {handoff.fromName} by itself.
                        </span>
                    ) : (
                        <BigButton
                            kind="done"
                            icon={<Check className="h-5 w-5" aria-hidden />}
                            label="Done"
                            sub={turnOn ? "send it back" : "I handled it"}
                            on={mode === "done"}
                            disabled={doneNow.isPending}
                            onClick={() => (turnOn ? doneNow.mutate() : setMode("done"))}
                            testId="handoff-done"
                        />
                    )}
                    <BigButton
                        kind="pink"
                        icon={<ArrowLeftToLine className="h-5 w-5" aria-hidden />}
                        label={`Hand back to ${handoff.fromName}`}
                        sub="with a note"
                        on={mode === "handed_back"}
                        onClick={() => setMode(mode === "handed_back" ? null : "handed_back")}
                        testId="handoff-hand-back"
                    />
                </BigRow>
            )}
            {iAmTo && mode && (
                <div className="mx-4 mb-4 grid gap-2 rounded-xl border-[1.5px] border-pink-300 bg-card p-3 sm:ml-[100px] sm:mr-5 dark:border-pink-800">
                    <Textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={2}
                        maxLength={1000}
                        autoFocus
                        className="text-[15px]"
                        placeholder={
                            mode === "done"
                                ? `Anything to tell ${handoff.fromName}? (not required)`
                                : `What should ${handoff.fromName} do now?`
                        }
                        aria-label="Your answer"
                    />
                    <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => setMode(null)}>
                            Cancel
                        </Button>
                        <Button
                            disabled={answer.isPending || (mode === "handed_back" && !note.trim())}
                            onClick={() => answer.mutate(mode)}
                            className={cn(
                                "text-[15px] font-bold text-white",
                                mode === "done"
                                    ? "bg-emerald-700 hover:bg-emerald-800"
                                    : "bg-pink-700 hover:bg-pink-800",
                            )}
                        >
                            {mode === "done" ? "Mark done" : `Hand back to ${handoff.fromName}`}
                        </Button>
                    </div>
                </div>
            )}
        </CardShell>
    );
}

// ─── Job page ───────────────────────────────────────────────────────────
/** The job page's hand-off strip. Shows only for jobs in Closeout. */
export function JobHandoffStrip({ jobId, jobName }: { jobId: string; jobName: string }) {
    const { isOffice, user } = useAuth();
    const viewerId: string | null = user?.id ?? null;
    const qc = useQueryClient();
    const inQueue = useQuery({
        queryKey: ["job-handoff-queue", jobId],
        queryFn: () =>
            api.get<{
                jobs: Array<{
                    id: string;
                    ball?: Ball | null;
                    notes?: JobNote[];
                    packet_sent_at?: string | null;
                    asks?: AskOption[];
                }>;
            }>(`/api/close-out/queue?jobId=${encodeURIComponent(jobId)}`),
        enabled: isOffice && !!jobId,
        retry: false,
        staleTime: 30_000,
    });
    const h = useJobHandoff(jobId, isOffice && (inQueue.data?.jobs?.length ?? 0) > 0);
    const seen = useMutation({
        mutationFn: (id: string) => api.post(`/api/close-out/handoffs/${id}/seen`, {}),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["close-out-handoff", jobId] });
            refreshCloseOutQueue(qc, { jobId });
        },
    });
    const [notesOpen, setNotesOpen] = useState(false);
    if (!isOffice || (inQueue.data?.jobs?.length ?? 0) === 0) return null;
    const open = h.data?.open ?? null;
    const reply = h.data?.reply ?? null;
    const row = inQueue.data!.jobs[0]!;
    return (
        <div className="overflow-hidden rounded-xl border border-border bg-card" data-testid="job-handoff-strip">
            {/* "Why isn't this out yet?" — the same line Closeout shows (10/16). */}
            <WhyStrip job={row} onNotes={() => setNotesOpen(true)} />
            {notesOpen && <JobNotesDialog jobId={jobId} jobName={jobName} onClose={() => setNotesOpen(false)} />}
            {open && <HandoffBanner jobId={jobId} handoff={open} viewerId={viewerId} />}
            {!open && reply && (
                <HandoffBanner
                    jobId={jobId}
                    handoff={reply}
                    viewerId={viewerId}
                    onGotIt={reply.fromUserId === viewerId ? () => seen.mutate(reply.id) : undefined}
                />
            )}
            {!open && (
                <div className="flex flex-wrap items-center gap-2 px-3.5 py-2 text-[12.5px]">
                    <span className="text-muted-foreground">Stuck on something only someone else can answer?</span>
                    <HandToButton jobId={jobId} jobName={jobName} className="ml-auto" asks={row.asks} />
                </div>
            )}
        </div>
    );
}
